import React, { useEffect, useState, useCallback, useMemo, useRef, useContext, PointerEvent as ReactPointerEvent } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutContext } from '../../contexts/LayoutContext';

import DetailFrame from '../shared/Frames/DetailFrame';
import ProjectBadges from '../Projects/ProjectBadges';
import PendingAccessRequests from '../Projects/PendingAccessRequests';
import Badge from '../shared/TailwindComponents/Badge';
import Button from '../shared/TailwindComponents/Button';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import {
  PiPlayLight,
  PiPlusLight,
  PiDownloadSimpleLight,
  PiTrashLight,
  PiUploadSimpleLight,
  PiFloppyDiskLight,
  PiSpinnerGapLight,
  PiFileTextLight,
  PiFilePdfLight,
  PiFileDocLight,
  PiFileXlsLight,
  PiFileCsvLight,
  PiWarningLight,
  PiXLight,
  PiTableLight,
  PiArrowCounterClockwiseLight,
  PiTextTLight,
  PiCalendarBlankLight,
  PiHashLight,
  PiToggleLeftLight,
  PiCurrencyDollarLight,
  PiDiamondLight,
  PiFlagLight,
  PiCheckLight,
  PiColumnsLight,
  PiUsersLight,
  PiChatTextLight,
  PiPencilSimpleLight,
  PiClockCounterClockwiseLight,
  PiLockLight,
  PiLockOpenLight,
} from 'react-icons/pi';

import {
  fetchBatchTableDetail,
  addColumn,
  updateColumn,
  deleteColumn,
  deleteDocument,
  startProcessing,
  exportTable,
  saveTableAsTemplate,
  updateBatchTable,
  resetTable,
  deleteBatchTable,
  streamTableDocument,
  processColumn,
  addTableCollaborator,
  removeTableCollaborator,
  updateTableCollaboratorAccess,
} from '../../store/actions/BatchTables/BatchTablesActions';

import { showModal, hideModal } from '../../store/actions/modalActions';
import { showSidePanel } from '../../store/actions/sidePanelActions';
import { TableDetail, TableColumn, TableRow, TableCell, TableStatus, ColumnDataType, UpdateColumnPayload } from '../../types/batchTables';
import CellDetailPanel from './CellDetailPanel';
import EditColumnContent from './EditColumnContent';
import { useTablePubSub } from './useTablePubSub';
import { Tooltip } from '@mui/material';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import { useLicenseGate } from '../../hooks/useLicenseGate';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, tableDetailTourSteps } from '../shared/FeatureTour';

// ─── Status colors ───────────────────────────────────────────────────────────

const statusColorMap: Record<TableStatus, BadgeColor> = {
  pending: 'gray',
  queued: 'orange',
  processing: 'blue',
  completed: 'green',
  failed: 'red',
  partial: 'yellow',
};

// ─── Column type icon mapping ────────────────────────────────────────────────

const columnTypeIcon: Record<ColumnDataType, React.ReactNode> = {
  text: <PiTextTLight className="w-3.5 h-3.5" />,
  date: <PiCalendarBlankLight className="w-3.5 h-3.5" />,
  number: <PiHashLight className="w-3.5 h-3.5" />,
  boolean: <PiToggleLeftLight className="w-3.5 h-3.5" />,
  currency: <PiCurrencyDollarLight className="w-3.5 h-3.5" />,
};

// ─── File type icon ──────────────────────────────────────────────────────────

const getFileIcon = (fileType: string | null): React.ReactNode => {
  const ext = (fileType || '').toLowerCase();
  if (ext === 'pdf') return <PiFilePdfLight className="w-4 h-4 text-red-500 flex-shrink-0" />;
  if (ext === 'docx' || ext === 'doc') return <PiFileDocLight className="w-4 h-4 text-blue-500 flex-shrink-0" />;
  if (ext === 'xlsx' || ext === 'xls') return <PiFileXlsLight className="w-4 h-4 text-green-600 flex-shrink-0" />;
  if (ext === 'csv') return <PiFileCsvLight className="w-4 h-4 text-green-500 flex-shrink-0" />;
  return <PiFileTextLight className="w-4 h-4 text-gray-400 flex-shrink-0" />;
};

// ─── Smart value rendering ───────────────────────────────────────────────────

const isBooleanValue = (val: string): boolean => {
  const v = val.trim().toLowerCase();
  return ['true', 'false', 'yes', 'no'].includes(v);
};

const isTruthyBoolean = (val: string): boolean => {
  const v = val.trim().toLowerCase();
  return v === 'true' || v === 'yes';
};

const CellValueRenderer: React.FC<{ value: string | null; dataType?: ColumnDataType }> = ({ value, dataType }) => {
  if (!value) return <span className="text-gray-300 dark:text-gray-600">—</span>;

  // Boolean detection (explicit type or auto-detect)
  if (dataType === 'boolean' || isBooleanValue(value)) {
    const truthy = isTruthyBoolean(value);
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
        truthy
          ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
          : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
      }`}>
        {truthy && <PiCheckLight className="w-3 h-3" />}
        {truthy ? 'Yes' : 'No'}
        {!truthy && <PiFlagLight className="w-3 h-3" />}
      </span>
    );
  }

  // Default text — uniform style for all non-boolean values
  return (
    <span className="line-clamp-2 text-gray-700 dark:text-gray-300 text-xs leading-relaxed">
      {value}
    </span>
  );
};

// ─── Skeleton loader for processing cells ────────────────────────────────────

const CellSkeleton: React.FC = () => (
  <div className="space-y-1.5">
    <div
      className="h-3 rounded-full animate-skeleton-shimmer"
      style={{
        width: `${60 + Math.random() * 30}%`,
        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
        backgroundSize: '200% 100%',
      }}
    />
    <div
      className="h-3 rounded-full animate-skeleton-shimmer"
      style={{
        width: `${40 + Math.random() * 20}%`,
        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
        backgroundSize: '200% 100%',
        animationDelay: '0.15s',
      }}
    />
  </div>
);

// Default column widths
const DEFAULT_DOC_COL_WIDTH = 260;
const DEFAULT_QUERY_COL_WIDTH = 220;
const MIN_COL_WIDTH = 100;

// ─── Component ───────────────────────────────────────────────────────────────

const BatchTableDetail: React.FC = () => {
  const { tableId } = useParams<{ tableId: string }>();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { checkAiFeatureGate, hasAiFeatureAccess } = useLicenseGate();
  const { setIsContentScrollable } = useContext(LayoutContext);

  // Redux state
  const table = useSelector((state: RootState) => state.batchTables.currentTable);
  const isLoadingDetail = useSelector((state: RootState) => state.batchTables.isLoadingDetail);
  const isUploading = useSelector((state: RootState) => state.batchTables.isUploading);
  const isProcessing = useSelector((state: RootState) => state.batchTables.isProcessing);
  const userInfo = useSelector((state: RootState) => state.user.userInfo);

  // Local state
  const [selectedCell, setSelectedCell] = useState<{ row: TableRow; column: TableColumn; cell: TableCell | null } | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [auditTarget, setAuditTarget] = useState<{type: string; id: string; name: string} | null>(null);
  const [isDocColLocked, setIsDocColLocked] = useState(false);

  // Track whether the initial fetch for THIS tableId has completed
  const hasInitiallyFetched = useRef(false);

  // Track cells that were already completed when the table first loaded,
  // so we only animate newly-completed cells (from PubSub during processing).
  const initialCompletedCells = useRef<Set<string>>(new Set());

  // Column widths for resizing
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const resizingRef = useRef<{ colKey: string; startX: number; startWidth: number } | null>(null);

  const getColWidth = useCallback((colKey: string, defaultWidth: number) => {
    return columnWidths[colKey] || defaultWidth;
  }, [columnWidths]);

  const handleResizeStart = useCallback((colKey: string, e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = columnWidths[colKey] || (colKey === 'document' ? DEFAULT_DOC_COL_WIDTH : DEFAULT_QUERY_COL_WIDTH);
    resizingRef.current = { colKey, startX, startWidth };

    const handleMove = (moveEvent: globalThis.PointerEvent) => {
      if (!resizingRef.current) return;
      const delta = moveEvent.clientX - resizingRef.current.startX;
      const newWidth = Math.max(MIN_COL_WIDTH, resizingRef.current.startWidth + delta);
      setColumnWidths(prev => ({ ...prev, [resizingRef.current!.colKey]: newWidth }));
    };

    const handleUp = () => {
      resizingRef.current = null;
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
    };

    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
  }, [columnWidths]);

  // Lock scroll
  useEffect(() => {
    setIsContentScrollable(false);
    return () => { setIsContentScrollable(true); };
  }, [setIsContentScrollable]);

  // Track previous tableId to know when we're navigating to a DIFFERENT table
  const prevTableIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const isDifferentTable = prevTableIdRef.current !== tableId;
    prevTableIdRef.current = tableId;

    // Clear stale data only when navigating to a different table
    if (isDifferentTable) {
      hasInitiallyFetched.current = false;
      initialCompletedCells.current = new Set();
      setSelectedCell(null);
      setSelectedRows(new Set());
      setColumnWidths({});
      // Always null-out stale table data so the skeleton shows
      dispatch({ type: 'BATCH_TABLES_SET_DETAIL', payload: null });
    }

    if (tableId) {
      dispatch(fetchBatchTableDetail(tableId) as any).then(() => {
        hasInitiallyFetched.current = true;
      });
    }
  }, [dispatch, tableId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Snapshot completed cells whenever the table data arrives from a full fetch
  // so we know which cells NOT to animate (they were already done).
  useEffect(() => {
    if (!table || !hasInitiallyFetched.current) return;
    // Only snapshot once per table load (not on every PubSub cell update)
    if (initialCompletedCells.current.size > 0) return;
    const completed = new Set<string>();
    for (const row of table.rows) {
      for (const [colId, cell] of Object.entries(row.cells)) {
        if (cell && cell.status === 'completed') {
          completed.add(`${row.id}-${colId}`);
        }
      }
    }
    if (completed.size > 0) {
      initialCompletedCells.current = completed;
    }
  }, [table]);

  // Real-time PubSub: always connected while on the detail page so we never
  // miss events when column re-processing or row processing starts.
  useTablePubSub(tableId, !!table);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'table-detail',
    steps: tableDetailTourSteps,
    readyToShow: !isLoadingDetail && !!table && table.columns.length > 0,
    autoStartDelay: 600,
  });

  // ─── Breadcrumbs ─────────────────────────────────────────────────────

  const breadcrumbs = useMemo(
    () => [
      { name: 'Tables', href: '/tables', current: false },
      { name: table?.name || 'Loading...', href: '', current: true },
    ],
    [table?.name]
  );

  // ─── Actions ─────────────────────────────────────────────────────────

  const handleUploadClick = useCallback(() => {
    if (!tableId) return;
    dispatch(showSidePanel({
      type: 'addTableDocument',
      showSidePanel: true,
      width: '50%',
      message: t('Upload Documents'),
      icon: <PiUploadSimpleLight className="w-5 h-5 text-primary-500" />,
      subMsg: t('Add documents to this table for AI analysis.'),
      tableUuid: tableId,
    }) as any);
  }, [dispatch, tableId, t]);

  const handleAddColumn = useCallback(() => {
    if (!tableId) return;
    dispatch(showModal({
      type: 'addTableColumn',
      message: t('Add Column'),
      showModal: true,
      icon: <PiColumnsLight className="w-5 h-5 text-primary-500" />,
      tableUuid: tableId,
      onConfirm: async (data: { name: string; query?: string; data_type: ColumnDataType }) => {
        await dispatch(addColumn(tableId, data) as any);
      },
    }) as any);
  }, [dispatch, tableId, t]);

  const handleStartProcessing = useCallback(() => {
    if (!tableId || !table) return;
    if (!checkAiFeatureGate('tables_ai')) return;

    if (table.column_count === 0 || table.document_count === 0) {
      dispatch(showModal({
        type: 'confirmation',
        message: table.column_count === 0
          ? t('Missing Columns')
          : t('Missing Documents'),
        subMsg: table.column_count === 0
          ? t('Please add at least one column (query) before processing.')
          : t('Please upload at least one document before processing.'),
        showModal: true,
        onConfirm: () => dispatch(hideModal()),
      }) as any);
      return;
    }

    dispatch(showModal({
      type: 'confirmation',
      message: t('Start AI Processing'),
      subMsg: t(`This will process ${table.document_count} document${table.document_count > 1 ? 's' : ''} across ${table.column_count} column${table.column_count > 1 ? 's' : ''}, creating ${table.document_count * table.column_count} AI queries.`),
      showModal: true,
      onConfirm: async () => {
        dispatch(hideModal());
        // Clear the snapshot so all newly-completed cells animate
        initialCompletedCells.current = new Set();
        await dispatch(startProcessing(tableId) as any);
      },
      onCancel: () => dispatch(hideModal()),
    }) as any);
  }, [dispatch, tableId, table]);

  const handleExportCSV = useCallback(() => {
    if (tableId) dispatch(exportTable(tableId, 'csv') as any);
  }, [dispatch, tableId]);

  const handleExportJSON = useCallback(() => {
    if (tableId) dispatch(exportTable(tableId, 'json') as any);
  }, [dispatch, tableId]);

  const handleReset = useCallback(() => {
    if (!tableId) return;
    dispatch(showModal({
      type: 'confirmation',
      message: t('Reset Table'),
      subMsg: t('This will clear all AI results and return the table to pending status, allowing you to re-process.'),
      showModal: true,
      onConfirm: async () => {
        dispatch(hideModal());
        initialCompletedCells.current = new Set();
        await dispatch(resetTable(tableId) as any);
      },
      onCancel: () => dispatch(hideModal()),
    }) as any);
  }, [dispatch, tableId]);

  const handleDeleteTable = useCallback(() => {
    if (!tableId) return;
    dispatch(showModal({
      type: 'confirmation',
      message: t('Delete Table'),
      subMsg: t('This will permanently delete this table and all associated documents. This action cannot be undone.'),
      icon: <PiTrashLight className='w-6 h-6 text-red-600' />,
      isDest: true,
      showModal: true,
      onConfirm: async () => {
        dispatch(hideModal());
        const success = await dispatch(deleteBatchTable(tableId) as any);
        if (success) {
          history.push('/tables');
        }
      },
      onCancel: () => dispatch(hideModal()),
    }) as any);
  }, [dispatch, tableId, history]);

  const handleSaveTemplate = useCallback(() => {
    if (!tableId || !table) return;
    dispatch(showSidePanel({
      type: 'saveTableTemplate',
      showSidePanel: true,
      width: '50%',
      message: t('Save as Template'),
      icon: <PiFloppyDiskLight className="w-5 h-5 text-primary-500" />,
      subMsg: t('Save the current column structure as a reusable template.'),
      tableUuid: tableId,
      defaultName: `${table.name} Template`,
    }) as any);
  }, [dispatch, tableId, table, t]);

  const handleDeleteDocument = useCallback(
    (rowId: number) => {
      dispatch(showModal({
        type: 'confirmation',
        message: t('Remove Document'),
        subMsg: t('This will remove this document and all its associated cells. This action cannot be undone.'),
        icon: <PiTrashLight className='w-6 h-6 text-red-600' />,
        isDest: true,
        showModal: true,
        onConfirm: async () => {
          await dispatch(deleteDocument(tableId, rowId) as any);
          setSelectedRows(prev => { const next = new Set(prev); next.delete(rowId); return next; });
          dispatch(hideModal());
        },
        onCancel: () => dispatch(hideModal()),
      }) as any);
    },
    [dispatch, tableId, t]
  );

  const handleDeleteSelectedDocuments = useCallback(() => {
    if (selectedRows.size === 0) return;
    dispatch(showModal({
      type: 'confirmation',
      message: t(`Remove ${selectedRows.size} Selected Document${selectedRows.size > 1 ? 's' : ''}`),
      subMsg: t(`This will remove ${selectedRows.size} selected document${selectedRows.size > 1 ? 's' : ''} and all their associated cells. This action cannot be undone.`),
      icon: <PiTrashLight className='w-6 h-6 text-red-600' />,
      isDest: true,
      showModal: true,
      onConfirm: async () => {
        dispatch(hideModal());
        for (const rowId of Array.from(selectedRows)) {
          await dispatch(deleteDocument(tableId, rowId) as any);
        }
        setSelectedRows(new Set());
      },
      onCancel: () => dispatch(hideModal()),
    }) as any);
  }, [dispatch, tableId, selectedRows, t]);

  const handleToggleRow = useCallback((rowId: number) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  const handleToggleAllRows = useCallback((allRowIds: number[]) => {
    setSelectedRows(prev => {
      if (prev.size === allRowIds.length) return new Set();
      return new Set(allRowIds);
    });
  }, []);

  const handleOpenDocument = useCallback(async (row: TableRow) => {
    if (!tableId) return;
    const fileName = row.file_name;
    const ext = (row.file_type || '').toLowerCase();

    // Map extension to MIME content type for the reader
    const contentTypeMap: Record<string, string> = {
      pdf: 'application/pdf',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      doc: 'application/msword',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      xls: 'application/vnd.ms-excel',
      pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      ppt: 'application/vnd.ms-powerpoint',
      txt: 'text/plain',
      csv: 'text/csv',
      html: 'text/html',
      htm: 'text/html',
    };
    const contentType = contentTypeMap[ext] || 'application/octet-stream';

    try {
      // Show reader side panel with loading state (same pattern as Sources)
      dispatch(showSidePanel({
        type: 'reader',
        showSidePanel: true,
        width: '85%',
        loading: true,
        message: fileName,
        subMsg: t('Document Preview'),
        matterId: '',
        sourceType: 'document',
        citation: {
          title: fileName,
          name: fileName,
          contentType,
        },
      }) as any);

      const blob = await streamTableDocument(tableId, row.id);

      // Update reader with the streamed blob (ReaderComponent handles DOCX/PDF/etc.)
      dispatch(showSidePanel({
        type: 'reader',
        showSidePanel: true,
        width: '85%',
        loading: false,
        message: fileName,
        subMsg: t('Document Preview'),
        matterId: '',
        sourceType: 'document',
        userAccessLevel: 'view',
        citation: {
          title: fileName,
          name: fileName,
          blob: blob,
          blobObject: blob,
          contentType,
        },
      }) as any);
    } catch (err) {
      console.error('Failed to open document:', err);
    }
  }, [dispatch, tableId, t]);

  const handleDeleteColumn = useCallback(
    (columnId: number) => {
      dispatch(showModal({
        type: 'confirmation',
        message: t('Delete Column'),
        subMsg: t('This will delete this column and all associated answers. This action cannot be undone.'),
        icon: <PiTrashLight className='w-6 h-6 text-red-600' />,
        isDest: true,
        showModal: true,
        onConfirm: async () => {
          await dispatch(deleteColumn(tableId, columnId) as any);
          dispatch(hideModal());
        },
        onCancel: () => dispatch(hideModal()),
      }) as any);
    },
    [dispatch, tableId, t]
  );

  const handleEditColumn = useCallback(
    (col: TableColumn) => {
      dispatch(showModal({
        type: 'editTableColumn',
        message: t('Edit Column'),
        showModal: true,
        icon: <PiPencilSimpleLight className="w-5 h-5 text-primary-500" />,
        tableUuid: tableId,
        columnToEdit: col,
        onConfirm: async (payload: UpdateColumnPayload, queryChanged?: boolean) => {
          await dispatch(updateColumn(tableId, col.id, payload) as any);
          dispatch(hideModal());

          if (queryChanged) {
            // Ask the user whether to re-process
            dispatch(showModal({
              type: 'confirmation',
              message: t('Re-process Column'),
              subMsg: t('The query or data type was changed. Re-processing will update all answers for this column across all documents.'),
              showModal: true,
              onConfirm: async () => {
                await dispatch(processColumn(tableId, col.id) as any);
                dispatch(hideModal());
              },
              onCancel: () => dispatch(hideModal()),
            }) as any);
          }
        },
      }) as any);
    },
    [dispatch, tableId, t]
  );

  const handleSaveName = useCallback(async () => {
    if (editName.trim() && tableId) {
      await dispatch(updateBatchTable(tableId, { name: editName.trim() }) as any);
    }
    setIsEditingName(false);
  }, [dispatch, tableId, editName]);

  // ─── Header actions ──────────────────────────────────────────────────

  const headerActions = useMemo(() => {
    if (!table) return [];

    const actions = [];

    const canProcess = table.status === 'pending' || table.status === 'failed' || table.status === 'partial';

    if (canProcess) {
      actions.push({
        label: t('Run all'),
        icon: <PiPlayLight className="w-4 h-4" />,
        onClick: handleStartProcessing,
        variant: 'primary' as const,
        disabled: !hasAiFeatureAccess('tables_ai'),
      });
    }

    return actions;
  }, [table, handleStartProcessing, handleReset, t]);

  const headerDropdownActions = useMemo(() => {
    if (!table) return [];
    const actions: any[] = [];

    actions.push({
      label: t('Manage Access'),
      icon: <PiUsersLight className="w-4 h-4 text-gray-900 dark:text-gray-600" />,
      onClick: () => {
        dispatch(showSidePanel({
          type: 'manageResourceCollaborators',
          showSidePanel: true,
          width: '50%',
          message: t('Manage Access'),
          resourceUuid: tableId,
          resourceType: 'table',
          collaborators: table.shared_with || [],
          currentUserUuid: userInfo?.UserUID,
          canAdmin: true,
          addCollaborator: async (uuid: string, userUuid: string, access: string) => {
            await dispatch(addTableCollaborator(uuid, userUuid, access) as any);
          },
          removeCollaborator: async (uuid: string, collabUuid: string) => {
            await dispatch(removeTableCollaborator(uuid, collabUuid) as any);
          },
          updateAccess: async (uuid: string, collabUuid: string, access: string) => {
            await dispatch(updateTableCollaboratorAccess(uuid, collabUuid, access) as any);
          },
          onUpdate: async () => {
            if (tableId) dispatch(fetchBatchTableDetail(tableId) as any);
          },
        }));
      },
      variant: 'outline' as const,
    });

    actions.push({
      label: t('Activity'),
      icon: <PiClockCounterClockwiseLight className="w-4 h-4 text-gray-900 dark:text-gray-600" />,
      onClick: () => setAuditTarget({ type: 'table', id: tableId, name: table.name || '' }),
      variant: 'outline' as const,
    });

    actions.push({
      label: t('Delete Table'),
      icon: <PiTrashLight className="w-4 h-4 text-red-500" />,
      onClick: handleDeleteTable,
      variant: 'destructive' as const,
    });

    return actions;
  }, [table, handleDeleteTable, handleReset, t, dispatch, tableId, userInfo?.UserUID]);

  // ─── Progress bar ────────────────────────────────────────────────────

  const progressPercent = table && table.cells_total > 0
    ? Math.round((table.cells_completed / table.cells_total) * 100)
    : 0;

  // Map shared_with to CollaboratorBadge format for the header
  const collaboratorBadges = useMemo(() => {
    if (!table?.shared_with) return [];
    return table.shared_with.map((u) => ({
      user_uuid: String(u.user_id),
      name: u.name,
      initials: u.initials,
      access: u.access || 'view',
      email: u.email || null,
      avatar_url: u.profile_picture || null,
    }));
  }, [table?.shared_with]);

  // ─── Loading / error states ──────────────────────────────────────────

  // Show skeleton when:
  // 1. Loading with no table data yet (initial navigation)
  // 2. Haven't completed the first fetch yet (before useEffect fires)
  const showSkeleton = (!hasInitiallyFetched.current && !table) || (isLoadingDetail && !table);
  if (showSkeleton) {
    return (
      <DetailFrame breadcrumbs={[{ name: 'Tables', href: '/tables', current: false }, { name: 'Loading...', href: '', current: true }]}>
        {/* Skeleton toolbar */}
        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2 flex-shrink-0 bg-white dark:bg-gray-900">
          <div className="h-5 w-20 rounded-full bg-gray-100 dark:bg-gray-800 animate-fadePulse" />
          <div className="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-1" />
          <div className="h-7 w-28 rounded bg-gray-100 dark:bg-gray-800 animate-fadePulse" />
          <div className="h-7 w-24 rounded bg-gray-100 dark:bg-gray-800 animate-fadePulse" />
          <div className="flex-1" />
          <div className="h-4 w-16 rounded bg-gray-100 dark:bg-gray-800 animate-fadePulse" />
        </div>
        {/* Skeleton table */}
        <div className="flex-1 overflow-hidden">
          <table className="border-collapse w-full" style={{ tableLayout: 'fixed' }}>
            <thead className="sticky top-0 z-10">
              <tr className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <th className="bg-gray-50 dark:bg-gray-900 w-[36px] px-1 py-3 border-r border-gray-200" />
                <th className="bg-gray-50 dark:bg-gray-900 w-[32px] px-1 py-3 border-r border-gray-200" />
                <th className="bg-gray-50 dark:bg-gray-900 px-3 py-3 border-r border-gray-200 w-[260px]">
                  <div className="h-3 w-20 rounded bg-gray-200 dark:bg-gray-700 animate-fadePulse" />
                </th>
                {[1, 2, 3, 4].map(i => (
                  <th key={i} className="bg-white dark:bg-gray-900 px-3 py-3 border-r border-gray-100 w-[220px]">
                    <div className="h-3 rounded bg-gray-200 dark:bg-gray-700 animate-fadePulse" style={{ width: `${50 + i * 10}%` }} />
                    <div className="h-2 w-3/4 rounded bg-gray-100 dark:bg-gray-800 animate-fadePulse mt-1.5" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5, 6, 7, 8].map(row => (
                <tr key={row} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="bg-gray-50/80 px-1 py-3 border-r border-gray-200 w-[36px]">
                    <div className="h-3.5 w-3.5 rounded bg-gray-200 dark:bg-gray-700 animate-fadePulse mx-auto" />
                  </td>
                  <td className="bg-gray-50/80 px-1 py-3 border-r border-gray-200 w-[32px]">
                    <div className="h-3 w-4 rounded bg-gray-200 dark:bg-gray-700 animate-fadePulse mx-auto" />
                  </td>
                  <td className="bg-gray-50/80 px-3 py-3 border-r border-gray-200">
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 rounded bg-gray-200 dark:bg-gray-700 animate-fadePulse flex-shrink-0" />
                      <div className="h-3 rounded bg-gray-200 dark:bg-gray-700 animate-fadePulse" style={{ width: `${55 + row * 5}%` }} />
                    </div>
                  </td>
                  {[1, 2, 3, 4].map(col => (
                    <td key={col} className="px-3 py-3 border-r border-gray-100">
                      <div className="space-y-1.5">
                        <div className="h-3 rounded-full animate-skeleton-shimmer" style={{ width: `${50 + (row + col) * 5}%`, background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)', backgroundSize: '200% 100%' }} />
                        <div className="h-3 rounded-full animate-skeleton-shimmer" style={{ width: `${30 + (row + col) * 4}%`, background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', animationDelay: '0.15s' }} />
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DetailFrame>
    );
  }

  // Not found (loading finished but no table)
  if (!table) {
    return (
      <DetailFrame breadcrumbs={breadcrumbs} notFound>
        <div />
      </DetailFrame>
    );
  }

  // This point is only reached when isLoadingDetail is false AND table exists

  const columns = table.columns.slice().sort((a: TableColumn, b: TableColumn) => a.sort_order - b.sort_order);
  const rows = table.rows.slice().sort((a: TableRow, b: TableRow) => a.sort_order - b.sort_order);
  const isActive = ['queued', 'processing'].includes(table.status);

  return (<>
    <DetailFrame
      breadcrumbs={breadcrumbs}
      actions={headerActions}
      dropdownActions={headerDropdownActions}
      collaborators={collaboratorBadges}
      additionalHeaderElements={
        <>
          <ProjectBadges resourceType="table" resourceUuid={tableId} />
          <PendingAccessRequests resourceType="table" resourceUuid={tableId} />
        </>
      }
    >
      {/* Toolbar */}
      <div data-tour-id="table-detail-toolbar" className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2 flex-shrink-0 bg-white dark:bg-gray-900">
        {/* Status badge */}
        <Badge color={statusColorMap[table.status] || 'gray'}>
          {table.status.charAt(0).toUpperCase() + table.status.slice(1)}
        </Badge>

        <div className="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-1" />

        {/* Toolbar action buttons */}
        <Button variant="outline" size="small" onClick={handleUploadClick} icon={<PiUploadSimpleLight className="w-3.5 h-3.5" />}>
          {t('Add documents')}
        </Button>
        <Button variant="outline" size="small" onClick={handleAddColumn} icon={<PiColumnsLight className="w-3.5 h-3.5" />}>
          {t('Add column')}
        </Button>

        {/* {table.cells_completed > 0 && (
          <>
            <Button variant="outline" size="small" onClick={handleExportCSV} icon={<PiDownloadSimpleLight className="w-3.5 h-3.5" />}>
              {t('CSV')}
            </Button>
            {/* <Button variant="outline" size="small" onClick={handleExportJSON} icon={<PiDownloadSimpleLight className="w-3.5 h-3.5" />}>
              {t('JSON')}
            </Button> 
          </>
        )} */}

        {table.column_count > 0 && (
          <Button variant="outline" size="small" onClick={handleSaveTemplate} icon={<PiFloppyDiskLight className="w-3.5 h-3.5" />}>
            {t('Save as Template')}
          </Button>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Progress indicator (compact) */}
        {table.cells_total > 0 && (
          <div className="flex items-center gap-2">
            <div className="w-24 h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500 bg-blue-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span className="text-[11px] text-gray-400 dark:text-gray-500 font-mono tabular-nums whitespace-nowrap">
              {table.cells_completed}/{table.cells_total}
            </span>
          </div>
        )}

        {isActive && (
          <div className="flex items-center gap-1.5 text-blue-500 ml-1">
            <PiSpinnerGapLight className="w-3.5 h-3.5 animate-spin" />
            <span className="text-[11px] font-medium">Processing…</span>
          </div>
        )}

        {isUploading && (
          <div className="flex items-center gap-1.5 text-orange-500 ml-1">
            <PiSpinnerGapLight className="w-3.5 h-3.5 animate-spin" />
            <span className="text-[11px] font-medium">Uploading…</span>
          </div>
        )}

        {/* Selection actions */}
        {selectedRows.size > 0 && (
          <>
            <div className="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-1" />
            <span className="text-[11px] font-medium text-gray-600 dark:text-gray-400">
              {selectedRows.size} selected
            </span>
            <Button variant="destructive" size="small" onClick={handleDeleteSelectedDocuments} icon={<PiTrashLight className="w-3.5 h-3.5" />}>
              {t('Delete')}
            </Button>
            <button
              onClick={() => setSelectedRows(new Set())}
              className="text-[11px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 px-1.5 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              {t('Clear')}
            </button>
          </>
        )}

        {/* Info */}
        <div className="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-1" />
        <span className="text-[11px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
          {table.document_count} doc{table.document_count !== 1 ? 's' : ''} · {table.column_count} col{table.column_count !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Grid View */}
      <div
        className="flex-1 min-h-0 min-w-0 max-h-[calc(100vh-100px)] custom-scrollbar-main relative"
        style={{ overflowX: 'auto', overflowY: 'auto' }}
      >
        {/* Tour anchor for the grid area */}
        <div
          data-tour-id="table-detail-grid"
          className="absolute inset-x-0 top-0 pointer-events-none"
          style={{ height: 120 }}
        />
        {columns.length === 0 && rows.length === 0 ? (
          /* Empty state */
          <div className="flex flex-col items-center justify-center h-full text-center px-6">
            <div className="w-16 h-16 rounded-2xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center mb-5">
              <PiTableLight className="w-8 h-8 text-gray-300 dark:text-gray-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-1">
              {t("Let's set up your table")}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-sm">
              {t('Add columns to define your AI queries, then upload documents for analysis.')}
            </p>
            <div className="flex gap-3">
              <Button variant="primary" size="small" onClick={handleAddColumn} icon={<PiColumnsLight />}>
                {t('Add Column')}
              </Button>
              <Button variant="outline" size="small" onClick={handleUploadClick} icon={<PiUploadSimpleLight />}>
                {t('Upload Documents')}
              </Button>
            </div>
          </div>
        ) : (
          /* Spreadsheet grid */
          <div className="inline-block min-w-full">
            <table className="border-collapse" style={{ tableLayout: 'fixed', minWidth: '100%' }}>
              <colgroup>
                <col style={{ width: 36 }} />
                <col style={{ width: 32 }} />
                <col style={{ width: getColWidth('document', DEFAULT_DOC_COL_WIDTH) }} />
                {columns.map((col: TableColumn) => (
                  <col key={col.id} style={{ width: getColWidth(`col-${col.id}`, DEFAULT_QUERY_COL_WIDTH) }} />
                ))}
                <col style={{ width: 40 }} />
              </colgroup>

              {/* ── Column headers ── */}
              <thead data-tour-id="table-detail-columns" className="sticky top-0 z-10">
                <tr className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  {/* Select-all */}
                  <th
                    className="bg-gray-50 dark:bg-gray-900 text-center px-3 py-2.5 border-r border-gray-200 dark:border-gray-800 select-none w-[36px]"
                    style={isDocColLocked ? { position: 'sticky', left: 0, top: 0, zIndex: 30 } : undefined}
                  >
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5 cursor-pointer"
                      checked={rows.length > 0 && selectedRows.size === rows.length}
                      onChange={() => handleToggleAllRows(rows.map(r => r.id))}
                      title={t('Select all')}
                    />
                  </th>

                  {/* Row # */}
                  <th
                    className="bg-gray-50 dark:bg-gray-900 text-center px-3 py-2.5 border-r border-gray-200 dark:border-gray-800 select-none w-[32px]"
                    style={isDocColLocked ? { position: 'sticky', left: 36, top: 0, zIndex: 30 } : undefined}
                  />

                  {/* Document header */}
                  <th
                    className="bg-gray-50 dark:bg-gray-900 text-left px-3 py-2.5 border-r border-gray-200 dark:border-gray-800 select-none relative"
                    style={{
                      width: getColWidth('document', DEFAULT_DOC_COL_WIDTH),
                      minWidth: getColWidth('document', DEFAULT_DOC_COL_WIDTH),
                      maxWidth: getColWidth('document', DEFAULT_DOC_COL_WIDTH),
                      ...(isDocColLocked
                        ? { position: 'sticky' as const, left: 60, top: 0, zIndex: 30, boxShadow: '6px 0 8px -2px rgba(0,0,0,0.08)' }
                        : {}),
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <PiFileTextLight className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 tracking-wide">
                        {t('Document')}
                      </span>
                      <Tooltip title={isDocColLocked ? t('Unlock document column') : t('Lock document column')} placement="top" arrow>
                        <button
                          onClick={() => setIsDocColLocked((v) => !v)}
                          aria-pressed={isDocColLocked}
                          className={`ml-auto mr-2 p-1 rounded transition-colors flex-shrink-0 ${
                            isDocColLocked
                              ? 'text-blue-600 bg-blue-50 dark:text-blue-300 dark:bg-blue-900/20'
                              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-800'
                          }`}
                        >
                          {isDocColLocked ? <PiLockLight className="w-3.5 h-3.5" /> : <PiLockOpenLight className="w-3.5 h-3.5" />}
                        </button>
                      </Tooltip>
                    </div>
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-400 active:bg-blue-500 z-30"
                      onPointerDown={(e) => handleResizeStart('document', e)}
                    />
                  </th>

                  {/* Query column headers */}
                  {columns.map((col: TableColumn) => {
                    const colWidth = getColWidth(`col-${col.id}`, DEFAULT_QUERY_COL_WIDTH);
                    return (
                    <th
                      key={col.id}
                      className="bg-white dark:bg-gray-900 text-left px-3 py-2.5 border-r border-gray-100 dark:border-gray-800 group relative select-none"
                      style={{ width: colWidth, minWidth: colWidth, maxWidth: colWidth }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">
                          {columnTypeIcon[col.data_type] || <PiDiamondLight className="w-3.5 h-3.5" />}
                        </span>
                        <Tooltip title={col.name} placement="top" arrow enterDelay={300}>
                          <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 truncate tracking-wide">
                            {col.name}
                          </span>
                        </Tooltip>
                        <button
                          onClick={() => handleEditColumn(col)}
                          className="opacity-0 group-hover:opacity-100 ml-auto p-0.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all flex-shrink-0"
                          title={t('Edit column')}
                        >
                          <PiPencilSimpleLight className="w-3 h-3 text-blue-400" />
                        </button>
                        <button
                          onClick={() => handleDeleteColumn(col.id)}
                          className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-all flex-shrink-0"
                          title={t('Delete column')}
                        >
                          <PiXLight className="w-3 h-3 text-red-400" />
                        </button>
                      </div>
                      {col.query && (
                        <Tooltip title={col.query} placement="bottom" arrow enterDelay={300}>
                          <p className="text-[10px] font-normal text-gray-400 dark:text-gray-600 truncate mt-0.5 leading-tight">
                            {col.query}
                          </p>
                        </Tooltip>
                      )}
                      <div
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-400 active:bg-blue-500 z-30"
                        onPointerDown={(e) => handleResizeStart(`col-${col.id}`, e)}
                      />
                    </th>
                    );
                  })}

                  {/* Add column */}
                  <th className="bg-white dark:bg-gray-900 px-2 py-2.5 w-10">
                    <Tooltip title={t('Add column')} placement="top" arrow>
                      <button
                        onClick={handleAddColumn}
                        className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      >
                        <PiPlusLight className="w-3.5 h-3.5 text-gray-400" />
                      </button>
                    </Tooltip>
                  </th>
                </tr>
              </thead>

              {/* ── Rows ── */}
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {rows.map((row: TableRow, rowIndex: number) => {
                  const isRowSelected = selectedRows.has(row.id);
                  const isRowProcessing = row.status === 'processing';
                  const isRowFailed = row.status === 'failed';
                  return (
                    <tr
                      key={row.id}
                      className={`transition-colors group/row ${
                        isRowSelected
                          ? 'bg-blue-50/50 dark:bg-blue-900/10'
                          : isRowFailed
                            ? 'bg-red-50/30 dark:bg-red-900/5'
                            : 'bg-white dark:bg-gray-900 hover:bg-gray-50/80 dark:hover:bg-gray-800/50'
                      }`}
                    >
                      {/* Checkbox */}
                      <td
                        className={`${isDocColLocked ? 'bg-gray-50 dark:bg-gray-900' : 'bg-gray-50/80 dark:bg-gray-900'} px-1 py-2 border-r border-gray-200 dark:border-gray-800 text-center w-[36px]`}
                        style={isDocColLocked ? { position: 'sticky', left: 0, zIndex: 2 } : undefined}
                      >
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5 cursor-pointer"
                          checked={isRowSelected}
                          onChange={() => handleToggleRow(row.id)}
                        />
                      </td>

                      {/* Row number */}
                      <td
                        className={`${isDocColLocked ? 'bg-gray-50 dark:bg-gray-900' : 'bg-gray-50/80 dark:bg-gray-900'} px-1 py-2 border-r border-gray-200 dark:border-gray-800 text-center w-[32px]`}
                        style={isDocColLocked ? { position: 'sticky', left: 36, zIndex: 2 } : undefined}
                      >
                        <span className="text-[11px] text-gray-400 dark:text-gray-600 font-mono">
                          {rowIndex + 1}
                        </span>
                      </td>

                      {/* Document name */}
                      <td
                        className={`${isDocColLocked ? 'bg-gray-50 dark:bg-gray-900' : 'bg-gray-50/80 dark:bg-gray-900'} px-3 py-2 border-r border-gray-200 dark:border-gray-800 group/doc`}
                        style={{
                          width: getColWidth('document', DEFAULT_DOC_COL_WIDTH),
                          minWidth: getColWidth('document', DEFAULT_DOC_COL_WIDTH),
                          maxWidth: getColWidth('document', DEFAULT_DOC_COL_WIDTH),
                          ...(isDocColLocked
                            ? { position: 'sticky' as const, left: 60, zIndex: 2, boxShadow: '6px 0 8px -2px rgba(0,0,0,0.08)' }
                            : {}),
                        }}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {getFileIcon(row.file_type)}
                          <Tooltip title={row.file_name} placement="top" arrow enterDelay={400}>
                            <button
                              onClick={() => handleOpenDocument(row)}
                              className="text-xs text-gray-800 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 truncate flex-1 min-w-0 text-left transition-colors"
                            >
                              {row.file_name}
                            </button>
                          </Tooltip>
                          {isRowProcessing && (
                            <PiSpinnerGapLight className="w-3.5 h-3.5 animate-spin text-blue-400 flex-shrink-0" />
                          )}
                          {isRowFailed && (
                            <Tooltip title={row.processing_error || 'Processing failed'} placement="top" arrow>
                              <span><PiWarningLight className="w-3.5 h-3.5 text-red-400 flex-shrink-0" /></span>
                            </Tooltip>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteDocument(row.id); }}
                            className="opacity-0 group-hover/row:opacity-100 p-0.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-all flex-shrink-0"
                            title={t('Remove document')}
                          >
                            <PiXLight className="w-3 h-3 text-gray-400 hover:text-red-400" />
                          </button>
                        </div>
                      </td>

                      {/* Data cells */}
                      {columns.map((col: TableColumn) => {
                        const cell = row.cells[String(col.id)] || null;
                        const isEmpty = !cell || cell.status === 'pending';
                        const isError = cell?.status === 'error';
                        const isCellSelected = selectedCell?.row.id === row.id && selectedCell?.column.id === col.id;
                        // Only animate cells that completed AFTER the initial load (live PubSub updates)
                        const cellKey = `${row.id}-${col.id}`;
                        const isNewlyCompleted = cell?.status === 'completed' && !initialCompletedCells.current.has(cellKey);
                        const colWidth = getColWidth(`col-${col.id}`, DEFAULT_QUERY_COL_WIDTH);

                        return (
                          <td
                            key={cellKey}
                            className={`px-3 py-2 border-r border-gray-100 dark:border-gray-800 text-xs cursor-pointer transition-colors max-h-[60px] overflow-hidden ${
                              isCellSelected
                                ? 'bg-blue-50 dark:bg-blue-900/20 ring-2 ring-inset ring-blue-400/50 dark:ring-blue-500/40'
                                : isError
                                  ? 'bg-red-50/40 dark:bg-red-900/5'
                                  : isNewlyCompleted
                                    ? 'animate-cell-in'
                                    : ''
                            }`}
                            style={{ width: colWidth, minWidth: colWidth, maxWidth: colWidth }}
                            onClick={() => setSelectedCell({ row, column: col, cell })}
                          >
                            {isEmpty ? (
                              (isActive || isRowProcessing) ? (
                                <CellSkeleton />
                              ) : (
                                <span className="text-gray-300 dark:text-gray-700">—</span>
                              )
                            ) : isError ? (
                              <Tooltip title={t('Error extracting value')} placement="top" arrow>
                                <span className="inline-flex items-center gap-1 text-red-400 italic text-[11px]">
                                  <PiWarningLight className="w-3 h-3" />
                                  {t('Error')}
                                </span>
                              </Tooltip>
                            ) : (
                              <Tooltip title={cell?.value || ''} placement="top" arrow enterDelay={400}>
                                <span className="flex items-center gap-1">
                                  <CellValueRenderer value={cell?.value || null} dataType={col.data_type} />
                                  {(cell?.comment_count ?? 0) > 0 && (
                                    <span className="inline-flex items-center gap-0.5 text-[9px] text-gray-400 dark:text-gray-500 flex-shrink-0 ml-auto">
                                      <PiChatTextLight className="w-2.5 h-2.5" />
                                      {cell!.comment_count}
                                    </span>
                                  )}
                                </span>
                              </Tooltip>
                            )}
                          </td>
                        );
                      })}

                      {/* Spacer */}
                      <td className="border-gray-100 dark:border-gray-800" />
                    </tr>
                  );
                })}

                {/* Empty row prompt */}
                {rows.length === 0 && columns.length > 0 && (
                  <tr>
                    <td colSpan={columns.length + 4} className="text-center py-16">
                      <PiUploadSimpleLight className="w-8 h-8 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t('Upload documents to start analyzing')}</p>
                      <Button variant="outline" size="small" onClick={handleUploadClick} icon={<PiUploadSimpleLight className="w-3.5 h-3.5" />}>
                        {t('Upload Documents')}
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cell Detail Side Panel */}
      {selectedCell && (
        <CellDetailPanel
          cell={selectedCell.cell}
          column={selectedCell.column}
          row={selectedCell.row}
          tableUuid={tableId}
          onClose={() => setSelectedCell(null)}
        />
      )}
    </DetailFrame>
    {auditTarget && (
      <>
        <div className="fixed inset-0 bg-black bg-opacity-20 z-40" onClick={() => setAuditTarget(null)} />
        <div className="fixed right-0 top-0 h-full w-96 bg-white dark:bg-gray-900 shadow-xl z-50 animate-slide-in-right">
          <AuditLogPanel
            entityType={auditTarget.type}
            entityId={auditTarget.id}
            entityName={auditTarget.name}
            onClose={() => setAuditTarget(null)}
          />
        </div>
      </>
    )}
    <FeatureTour
      steps={tableDetailTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      continuous
      showProgress
      showSkipButton
      disableOverlay
    />
  </>);
};

export default BatchTableDetail;